import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Users, ArrowRight, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "referral_banner_dismissed_v1";

/**
 * ReferralBanner — thin, dismissable strip promoting the referral program.
 * Shown on Home. Uses localStorage to remember dismissal so it doesn't
 * nag. Signed-in users see a personalized CTA taking them to Dashboard;
 * signed-out users get invited to sign up (which itself creates a ref code).
 */
export default function ReferralBanner() {
  const { user, openLogin } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  if (dismissed) return null;

  const primaryHref = user ? "/dashboard#referral-section" : null;
  const primaryOnClick = user ? undefined : () => openLogin("signup");

  return (
    <div
      className="mb-8 relative border-2 border-[#2d2a32] rounded-2xl px-5 py-4 hard-shadow-sm overflow-hidden"
      style={{ background: "linear-gradient(90deg, #fff2d6 0%, #ffe0d6 100%)" }}
      data-testid="home-referral-banner"
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="shrink-0 w-11 h-11 rounded-full border-2 border-[#2d2a32] bg-[#ff6b6b] text-white flex items-center justify-center">
            <Gift className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] font-bold flex items-center gap-2">
              <Users className="w-3 h-3" /> INVITE · EARN · REPEAT
            </div>
            <p className="text-[15px] md:text-base text-[#2d2a32] font-semibold leading-snug mt-0.5">
              Invite <span className="text-[#ff5b48]">3 friends</span> and unlock
              {" "}<span className="text-[#ff5b48]">1 month of Pro free</span> — every referral compounds.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <Link
              to={primaryHref}
              className="inline-flex items-center gap-2 h-11 px-5 border-2 border-[#2d2a32] rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white text-[11px] tracking-[0.22em] font-bold"
              data-testid="referral-banner-cta"
            >
              GRAB YOUR LINK <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <button
              onClick={primaryOnClick}
              className="inline-flex items-center gap-2 h-11 px-5 border-2 border-[#2d2a32] rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white text-[11px] tracking-[0.22em] font-bold"
              data-testid="referral-banner-cta"
            >
              SIGN UP TO START <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={dismiss}
            aria-label="Dismiss referral banner"
            className="w-9 h-9 rounded-full border-2 border-[#2d2a32] bg-white/70 hover:bg-white flex items-center justify-center"
            data-testid="referral-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
